import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Eye,
  EyeOff,
  Lock,
  ChevronLeft,
  Check,
  X,
  CheckCircle,
} from "lucide-react";
import Logo from "@/components/common/Logo";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast, useResetPassword } from "@/hooks";

const ResetPassword = () => {
  const params = new URLSearchParams(location.hash.substring(1));
  const accessToken = params.get("access_token");
  const refreshToken = params.get("refresh_token");
  const { toast } = useToast();
  const [isSuccess, setIsSuccess] = useState(false);
  const { mutate: resetPassword } = useResetPassword({
    onSuccess: () => {
      setIsSuccess(true);
    },
    onError: () => {
      toast({
        title: "Une erreur est survenue",
        description: "Impossible de réinitialiser le mot de passe.",
        variant: "destructive",
      });
    },
  });

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Validation des critères du mot de passe
  const hasMinLength = password.length >= 12;
  const hasLowerCase = /[a-z]/.test(password);
  const hasUpperCase = /[A-Z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSymbol = /[!@#$%^&*(),.?":{}|<>]/.test(password);
  const passwordsMatch = password === confirmPassword;

  const allCriteriaMet =
    hasMinLength &&
    hasLowerCase &&
    hasUpperCase &&
    hasNumber &&
    hasSymbol &&
    passwordsMatch &&
    password.length > 0;

  const ValidationItem = ({ met, text }: { met: boolean; text: string }) => (
    <div className="flex items-center gap-2 text-sm">
      {met ? (
        <Check className="h-4 w-4 text-green-500" />
      ) : (
        <X className="h-4 w-4 text-red-500" />
      )}
      <span className={met ? "text-green-700" : "text-red-700"}>{text}</span>
    </div>
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!allCriteriaMet) return;
    setIsSubmitting(true);
    await resetPassword({ password, accessToken, refreshToken });
    setIsSubmitting(false);
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen flex flex-col justify-center items-center p-6">
        <div className="w-full max-w-md flex flex-col items-center text-center">
          <div className="mb-8 rounded-full bg-green-100 p-4">
            <CheckCircle className="h-16 w-16 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-darkBlue-DEFAULT mb-4">
            Mot de passe réinitialisé
          </h1>
          <p className="text-gray-600 mb-8">
            Votre mot de passe a été réinitialisé. Vous pouvez vous connecter
            sur l'application.
          </p>
          <a
            href="cleomed-livreur://"
            className="text-cleoMed-primary hover:underline"
          >
            Ouvrir l'application
          </a>
          <p className="text-gray-400 text-xs mt-8">
            © {new Date().getFullYear()} CleoMed. Tous droits réservés.
          </p>
        </div>
      </div>
    );
  }

  if (!accessToken || !refreshToken) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">
            Lien invalide
          </h1>
          <p className="text-gray-600 mb-6">
            Ce lien de réinitialisation est invalide ou a expiré.
          </p>
          <a
            href="cleomed-livreur://"
            className="text-cleoMed-primary hover:underline"
          >
            Ouvrir l'application
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col justify-center items-center p-6">
      <div className="w-full max-w-md flex flex-col items-center">
        <Link
          to="/"
          className="self-start flex items-center text-gray-600 mb-8"
        >
          <ChevronLeft size={20} />
          <span>Retour</span>
        </Link>

        <div className="mb-12">
          <Logo size="md" />
        </div>

        <h1 className="text-3xl font-bold text-darkBlue-DEFAULT mb-2">
          Nouveau mot de passe
        </h1>
        <p className="text-gray-500 mb-8 text-center">
          Choisissez un nouveau mot de passe sécurisé
        </p>

        <form onSubmit={handleSubmit} className="w-full space-y-6">
          <div>
            <label
              htmlFor="password"
              className="block text-gray-700 font-medium mb-2"
            >
              Nouveau mot de passe
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-gray-400" />
              </div>
              <Input
                type={showPassword ? "text" : "password"}
                id="password"
                className="pl-10 h-14 rounded-xl bg-gray-50 border-gray-200"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <div>
            <label
              htmlFor="confirmPassword"
              className="block text-gray-700 font-medium mb-2"
            >
              Confirmer le mot de passe
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-gray-400" />
              </div>
              <Input
                type={showConfirmPassword ? "text" : "password"}
                id="confirmPassword"
                className="pl-10 h-14 rounded-xl bg-gray-50 border-gray-200"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded-xl space-y-2">
            <h3 className="font-medium text-gray-700 mb-3">
              Exigences du mot de passe :
            </h3>
            <ValidationItem met={hasMinLength} text="12 caractères minimum" />
            <ValidationItem met={hasLowerCase} text="Une lettre minuscule" />
            <ValidationItem met={hasUpperCase} text="Une lettre majuscule" />
            <ValidationItem met={hasNumber} text="Un chiffre" />
            <ValidationItem met={hasSymbol} text="Un caractère spécial" />
            <ValidationItem
              met={passwordsMatch}
              text="Les mots de passe correspondent"
            />
          </div>

          <Button
            type="submit"
            className="w-full h-14 bg-cleoMed-primary hover:bg-cleoMed-secondary text-white rounded-xl mb-6"
            disabled={!allCriteriaMet || isSubmitting}
          >
            {isSubmitting
              ? "Modification en cours..."
              : "Modifier le mot de passe"}
          </Button>
        </form>

        <p className="text-gray-400 text-xs mt-8 text-center">
          © {new Date().getFullYear()} CleoMed. Tous droits réservés.
        </p>
      </div>
    </div>
  );
};

export default ResetPassword;
